import React, { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logo from "../assets/icon.png";

// ---------- helpers (inside same file) ----------
const numberToWords = (num) => {
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const inWords = (n) => {
    n = Number(n || 0);
    if (n === 0) return "Zero";
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000)
      return (
        a[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 ? " " + inWords(n % 100) : "")
      );
    if (n < 100000)
      return (
        inWords(Math.floor(n / 1000)) +
        " Thousand" +
        (n % 1000 ? " " + inWords(n % 1000) : "")
      );
    if (n < 10000000)
      return (
        inWords(Math.floor(n / 100000)) +
        " Lakh" +
        (n % 100000 ? " " + inWords(n % 100000) : "")
      );
    return (
      inWords(Math.floor(n / 10000000)) +
      " Crore" +
      (n % 10000000 ? " " + inWords(n % 10000000) : "")
    );
  };

  return inWords(num).trim();
};

const formatIssueDate = (date = new Date()) =>
  date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

// ---------- certificate block (inside same file) ----------
const TuitionFeeCertificate = React.forwardRef((props, ref) => {
  const {
    school = {
      name: "ACHARYAKULAM",
      address: "TATA ROAD, NAMKUM, RANCHI 834010",
    },
    session = "2025-26",
    issueDate = "",
    parents = "",
    studentName = "",
    admissionNo = "",
    className = "",
    tuitionFee = 0,
    periodFrom = "APR",
    periodTo = "MAR",
    totalPaid = 0,
    amountInWords = "",
  } = props;

  return (
    <div ref={ref} style={styles.page}>
      <div style={styles.border}>
        <div style={styles.watermarkWrap} aria-hidden="true">
          <img src={logo} alt="" style={styles.watermarkImg} />
        </div>

        <div style={styles.content}>
          <div style={styles.headerRow}>
            <div style={styles.logoBox}>
              <img src={logo} alt="logo" style={styles.logoImg} />
            </div>

            <div style={styles.headerText}>
              <div style={styles.schoolName}>{school.name}</div>
              <div style={styles.schoolAddress}>{school.address}</div>
              <div style={styles.session}>Session ({session})</div>
            </div>

            <div style={styles.logoBox} />
          </div>

          <div style={styles.pillWrap}>
            <div style={styles.pill}>TUITION FEE CERTIFICATE</div>
          </div>

          <div style={styles.bigHeading}>TO WHOMSOEVER IT MAY CONCERN</div>
          <div style={styles.hr} />

          {/* Body text */}
          <div style={styles.bodyText}>
            This is to certify that <b>{parents}</b> have paid Tuition Fee for
            the study of their ward in our school as per details given below:-
          </div>

          {/* Table (fixed layout) */}
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Admission No.</th>
                  <th style={styles.th}>Ward Name</th>
                  <th style={styles.th}>Class</th>
                  <th style={styles.th}>Academic Session</th>
                  <th style={styles.th}>Tuition Fee</th>
                  <th style={styles.th}>Period</th>
                  <th style={styles.th}>Total Tuition Fee Paid</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td style={styles.tdBold}>{admissionNo}</td>
                  <td style={styles.tdBold}>{studentName}</td>
                  <td style={styles.tdBold}>{className}</td>
                  <td style={styles.tdBold}>{session}</td>
                  <td style={styles.tdBold}>{tuitionFee}</td>
                  <td style={styles.tdBold}>
                    {periodFrom} To {periodTo}
                  </td>
                  <td style={styles.tdBold}>{totalPaid}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={styles.amountWords}>
            <b>Amount Paid (In Words)</b> : {amountInWords}
          </div>

          <div style={styles.footerRow}>
            <div style={styles.footerCell}>
              <div style={styles.footerTop}>{issueDate}</div>
              <div style={styles.footerLabel}>Issue Date</div>
            </div>

            <div style={styles.footerCell}>
              <div style={styles.footerTop}>Dealing Clerk</div>
              <div style={styles.footerLabel}>Signature</div>
            </div>

            <div style={styles.footerCell}>
              <div style={styles.footerTop}>Principal</div>
              <div style={styles.footerLabel}>Signature</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// ---------- ONE PAGE component ----------
export default function TuitionFeeCertificatePage({
  feeDetails,
  student,
  buttonLabel = "Download Tuition Fee Certificate",
}) {
  const certRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  // Build certificate data (edit mapping as per your API fields)
  const MONTH_ORDER = [
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
    "JAN",
    "FEB",
    "MAR",
  ];

  // normalize "April" / "APR" / "Apr" etc → "APR"
  const normalizeMonth = (m) => {
    if (!m) return null;
    const x = String(m).trim().toUpperCase();

    const map = {
      APRIL: "APR",
      MAY: "MAY",
      JUNE: "JUN",
      JULY: "JUL",
      AUGUST: "AUG",
      SEPTEMBER: "SEP",
      SEPT: "SEP",
      OCTOBER: "OCT",
      NOVEMBER: "NOV",
      DECEMBER: "DEC",
      JANUARY: "JAN",
      FEBRUARY: "FEB",
      MARCH: "MAR",
    };

    if (map[x]) return map[x];
    // already like "APR"
    if (MONTH_ORDER.includes(x)) return x;

    // sometimes "Apr" etc
    const short = x.slice(0, 3);
    if (MONTH_ORDER.includes(short)) return short;

    return null;
  };

  const sortMonths = (months) =>
    [...new Set(months)]
      .filter(Boolean)
      .sort((a, b) => MONTH_ORDER.indexOf(a) - MONTH_ORDER.indexOf(b));

  const getPeriodFromMonths = (monthsSorted) => {
    if (!monthsSorted.length)
      return { periodFrom: "APR", periodTo: "MAR", periodLabel: "APR To MAR" };
    return {
      periodFrom: monthsSorted[0],
      periodTo: monthsSorted[monthsSorted.length - 1],
      periodLabel: `${monthsSorted[0]} To ${
        monthsSorted[monthsSorted.length - 1]
      }`,
    };
  };

  const certData = useMemo(() => {
    const session =
      feeDetails?.academic_session ||
      student?.academic_session ||
      feeDetails?.session ||
      "2023-2024";

    // ✅ Monthly Fee payments only (like FeeLedger category "Monthly Fee")
    const monthPaid = Array.isArray(feeDetails?.monthPaid)
      ? feeDetails.monthPaid
      : [];

    const paidMonthlyEntries = monthPaid
      .filter(
        (m) =>
          m?.monthlyFee?.paid && (m?.monthlyFee?.receiptNo || "").trim() !== "",
      )
      .map((m) => ({
        month: normalizeMonth(m?.month),
        amount: Number(m?.monthlyFee?.amount ?? 0),
        receiptNo: m?.monthlyFee?.receiptNo,
        date: m?.monthlyFee?.date,
      }))
      .filter((x) => x.amount > 0);

    // ✅ Total Paid = sum of monthlyFee amounts only
    const totalPaid = paidMonthlyEntries.reduce((sum, x) => sum + x.amount, 0);

    // ✅ Period from paid months
    const monthsSorted = sortMonths(paidMonthlyEntries.map((x) => x.month));
    const { periodFrom, periodTo, periodLabel } =
      getPeriodFromMonths(monthsSorted);

    // ✅ Tuition Fee (per month)
    // If amounts vary, choose the most common amount; fallback last amount
    let tuitionFee = 0;
    if (paidMonthlyEntries.length) {
      const freq = new Map();
      for (const e of paidMonthlyEntries)
        freq.set(e.amount, (freq.get(e.amount) || 0) + 1);
      tuitionFee =
        [...freq.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ??
        paidMonthlyEntries.at(-1).amount;
    }

    // ✅ Parents / Student / Admission / Class
    const parents = [
      student?.parentDetails?.[0]?.name, // Father
      student?.parentDetails?.[1]?.name, // Mother
    ]
      .filter(Boolean)
      .join(" & ")
      .toUpperCase();

    const studentName = (
      student?.student_name ||
      student?.name ||
      ""
    ).toUpperCase();

    const admissionNo = String(
      student?.admission_no || feeDetails?.admission_no || "",
    );

    const className = String(
      student?.currentClass ||
        student?.class ||
        student?.class_name ||
        student?.className ||
        feeDetails?.currentClass ||
        "",
    ).toUpperCase();

    const amountInWords =
      totalPaid > 0
        ? `Rupees ${numberToWords(totalPaid)} Only.`
        : "Rupees Zero Only.";

    return {
      session,
      issueDate: formatIssueDate(new Date()),

      parents,
      studentName,
      admissionNo,
      className,

      tuitionFee, // ✅ per month (most common)
      periodFrom, // ✅ from paid months
      periodTo, // ✅ to paid months
      periodLabel, // ✅ use this in UI if you want
      totalPaid, // ✅ monthly fee total only
      amountInWords,
    };
  }, [feeDetails, student]);

  const downloadPdf = async () => {
    try {
      if (!certRef.current) return;
      setDownloading(true);

      const canvas = await html2canvas(certRef.current, {
        scale: 3, // sharper
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      // A4 Landscape in mm
      const pdf = new jsPDF("l", "mm", "a4");
      const pageW = 297;
      const pageH = 210;

      const margin = 10; // 10mm margins
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      // keep aspect ratio
      const imgRatio = canvas.width / canvas.height;
      let renderW = usableW;
      let renderH = renderW / imgRatio;

      if (renderH > usableH) {
        renderH = usableH;
        renderW = renderH * imgRatio;
      }

      const x = (pageW - renderW) / 2;
      const y = (pageH - renderH) / 2;

      pdf.addImage(imgData, "PNG", x, y, renderW, renderH);
      pdf.save(
        `${certData.admissionNo || "student"}-tuition-fee-certificate.pdf`,
      );
    } catch (e) {
      console.error(e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="w-100">
      <div style={{ position: "fixed", left: "-99999px", top: 0 }}>
        <TuitionFeeCertificate ref={certRef} {...certData} />
      </div>

      <div className="my-2">
        <span
          className="bg-dark-soft fs-5 fw-normal rounded-pill ps-4 pe-5 py-2 cursor-pointer"
          onClick={downloadPdf}
          disabled={downloading}
        >
          <i className="bi bi-journal-text me-1"></i>{" "}
          {downloading ? "Downloading Certificate" : buttonLabel}
        </span>
      </div>
    </div>
  );
}

// ---------- styles ----------
const styles = {
  page: {
    width: "1123px",
    height: "794px",
    boxSizing: "border-box",
    background: "#fff",
    padding: "14px",
    fontFamily: "Arial, Helvetica, sans-serif",
    color: "#111",
  },

  border: {
    height: "100%",
    boxSizing: "border-box",
    border: "3px solid #111",
    padding: "30px",
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },

  content: {
    position: "relative",
    zIndex: 3,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },

  watermarkWrap: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    pointerEvents: "none",
  },
  watermarkImg: {
    width: "380px",
    height: "380px",
    objectFit: "contain",
    opacity: 0.08,
    filter: "grayscale(100%)",
  },

  headerRow: {
    display: "grid",
    gridTemplateColumns: "90px 1fr 90px",
    alignItems: "center",
  },
  logoBox: {
    width: "90px",
    height: "90px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoImg: { width: "95px", height: "95px", objectFit: "contain" },
  headerText: { textAlign: "center" },
  schoolName: { fontSize: "30px", fontWeight: 800, letterSpacing: "1px" },
  schoolAddress: { fontSize: "23px", marginTop: "-4px" },
  session: { fontSize: "23px", marginTop: "-4px" },

  tableWrap: {
    marginTop: "16px",
    width: "100%",
  },

  table: {
    width: "100%",
    display: "table",
    borderCollapse: "collapse",
    fontSize: "15px",
    tableLayout: "auto",
  },

  th: {
    border: "1px solid #333",
    padding: "10px 6px",
    fontWeight: 800,
    textAlign: "center",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  thSmall: {
    border: "1px solid #333",
    padding: "6px 6px",
    fontWeight: 800,
    textAlign: "center",
  },
  thSmallSpacer: {
    border: "1px solid #333",
    padding: "6px 6px",
  },
  tdBold: {
    border: "1px solid #333",
    padding: "12px 6px",
    textAlign: "center",
    fontWeight: 800,
    fontStyle: "italic",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },

  amountWords: {
    fontStyle: "italic",
    marginTop: "16px",
    fontSize: "15px",
    fontWeight: 700,
  },

  pillWrap: { display: "flex", justifyContent: "center", marginTop: "13px" },
  pill: {
    border: "2px solid #111",
    borderRadius: "12px",
    padding: "6px 70px",
    fontWeight: 700,
    fontSize: "16px",
  },

  bigHeading: {
    marginTop: "16px",
    textAlign: "center",
    fontSize: "26px",
    fontWeight: 800,
  },
  hr: { height: "1px", background: "#d8d8d8", marginTop: "10px" },

  bodyText: {
    marginTop: "30px",
    fontSize: "25px",
    lineHeight: "1.3",
    textAlign: "center",
    fontStyle: "italic",
    padding: "0 60px",
  },

  footerRow: {
    marginTop: "auto",
    display: "flex",
    justifyContent: "space-between",
    paddingTop: "35px",
    paddingBottom: "20px",
  },

  footerCell: { width: "33%", textAlign: "center" },
  footerTop: { fontSize: "17px", fontWeight: 700 },
  footerLabel: { marginTop: "-4px", fontSize: "17px", fontWeight: 700 },
};
